// Request Service Page - Customer Flow
import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { 
  ArrowLeft, 
  ArrowRight, 
  Upload, 
  CheckCircle, 
  MapPin,
  Phone,
  Mail,
  Camera,
  X,
  Wrench,
  Zap,
  Droplets,
  Flame,
  Home as HomeIcon,
  Paintbrush,
  Grid3X3,
  Hammer,
  Thermometer,
  Sun,
  DoorOpen,
  Square,
  CloudRain,
  Truck,
  Trash2,
  ChefHat
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { toast } from "../components/ui/sonner";
import MatchingScreen from "../components/MatchingScreen";
import axios from "axios";

const apiUrl = (path) => {
  const backendUrl = process.env.REACT_APP_BACKEND_URL || "";
  return `${backendUrl}/api${path}`;
};

const SERVICE_CATEGORIES = [
  { id: "electrician", name: "Elektriker", icon: Zap, color: "bg-yellow-100 text-yellow-700" },
  { id: "plumber", name: "Sanitär / SHK", icon: Droplets, color: "bg-blue-100 text-blue-700" },
  { id: "heating", name: "Heizungsbauer", icon: Flame, color: "bg-orange-100 text-orange-700" },
  { id: "roofer", name: "Dachdecker", icon: HomeIcon, color: "bg-red-100 text-red-700" },
  { id: "painter", name: "Maler & Lackierer", icon: Paintbrush, color: "bg-purple-100 text-purple-700" },
  { id: "tiler", name: "Fliesenleger", icon: Grid3X3, color: "bg-teal-100 text-teal-700" },
  { id: "drywall", name: "Trockenbau", icon: Square, color: "bg-gray-100 text-gray-700" },
  { id: "carpenter", name: "Tischler / Schreiner", icon: Hammer, color: "bg-amber-100 text-amber-700" },
  { id: "renovation", name: "Renovierung / Sanierung", icon: Wrench, color: "bg-indigo-100 text-indigo-700" },
  { id: "mold", name: "Schimmel / Feuchtigkeit", icon: CloudRain, color: "bg-cyan-100 text-cyan-700" },
  { id: "construction", name: "Bauunternehmen / Rohbau", icon: HomeIcon, color: "bg-stone-100 text-stone-700" },
  { id: "solar", name: "Photovoltaik / Solaranlagen", icon: Sun, color: "bg-yellow-100 text-yellow-700" },
  { id: "windows", name: "Fenster & Türen Montage", icon: DoorOpen, color: "bg-sky-100 text-sky-700" },
  { id: "insulation", name: "Wärmedämmung / Isolierung", icon: Thermometer, color: "bg-green-100 text-green-700" },
  { id: "flooring", name: "Bodenleger", icon: Square, color: "bg-brown-100 text-amber-700" },
  { id: "facade", name: "Fassadenbau / Fassadenreinigung", icon: HomeIcon, color: "bg-slate-100 text-slate-700" },
  { id: "gutters", name: "Regenrinnen & Entwässerung", icon: CloudRain, color: "bg-blue-100 text-blue-700" },
  { id: "moving", name: "Möbelmontage / Umzug", icon: Truck, color: "bg-green-100 text-green-700" },
  { id: "clearance", name: "Entrümpelung", icon: Trash2, color: "bg-rose-100 text-rose-700" },
  { id: "kitchen", name: "Küchenmontage", icon: ChefHat, color: "bg-orange-100 text-orange-700" },
];

const RequestServicePage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const preselectedCategory = searchParams.get("category");
  
  const [step, setStep] = useState(preselectedCategory ? 2 : 1);
  const [selectedCategory, setSelectedCategory] = useState(preselectedCategory || "");
  const [description, setDescription] = useState("");
  const [address, setAddress] = useState("");
  const [postalCode, setPostalCode] = useState("");
  const [city, setCity] = useState("");
  const [photos, setPhotos] = useState([]);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");
  const [submittedRequestId, setSubmittedRequestId] = useState(null);
  const [initialMatches, setInitialMatches] = useState([]);

  const totalSteps = 6;
  
  const handlePhotoUpload = (e) => {
    const files = Array.from(e.target.files);
    if (photos.length + files.length > 5) {
      setError("Maximal 5 Fotos erlaubt");
      return;
    }
    
    files.forEach((file) => {
      const reader = new FileReader();
      reader.onload = (event) => {
        setPhotos((prev) => [...prev, { file, preview: event.target.result }]);
      };
      reader.readAsDataURL(file);
    });
  };
  
  const removePhoto = (index) => {
    setPhotos((prev) => prev.filter((_, i) => i !== index));
  };
  
  const handleSubmit = async () => {
    setSubmitting(true);
    setError("");

    try {
      const token = typeof window !== "undefined" ? localStorage.getItem("token") : null;
      const headers = { "Content-Type": "application/json" };
      if (token) headers["Authorization"] = `Bearer ${token}`;

      const payload = {
        category: selectedCategory,
        service_name: SERVICE_CATEGORIES.find((c) => c.id === selectedCategory)?.name || selectedCategory,
        description,
        address,
        postal_code: postalCode,
        city,
        photos: photos.map((p) => p.preview).slice(0, 5),
        name,
        email,
        phone,
        urgency: "normal",
      };

      const res = await axios.post(apiUrl("/customer/requests"), payload, { headers });
      if (res.data?.ok) {
        toast.success("Anfrage erfolgreich gesendet", {
          description:
            res.data?.matched_providers_notified
              ? `${res.data.matched_providers_notified} passende Handwerker wurden benachrichtigt`
              : "Wir suchen passende Handwerker für Sie.",
        });
        setSubmitted(true);
      } else {
        setError(res.data?.detail || "Anfrage konnte nicht gesendet werden.");
      }
    } catch (err) {
      const msg =
        err?.response?.data?.detail ||
        err?.response?.data?.error ||
        "Fehler beim Senden der Anfrage. Bitte versuchen Sie es erneut.";
      setError(typeof msg === "string" ? msg : "Fehler beim Senden der Anfrage.");
    } finally {
      setSubmitting(false);
    }
  };
  
  const canProceed = () => {
    switch (step) {
      case 1: return selectedCategory !== "";
      case 2: return description.length >= 10;
      case 3: return postalCode.length >= 4;
      case 4: return true; // Photos optional
      case 5: return name && email;
      default: return true;
    }
  };

  if (submitted) {
    return (
      <MatchingScreen
        requestId={submittedRequestId}
        initialMatches={initialMatches}
        onReset={() => {
          setSubmitted(false);
          setStep(1);
          setSelectedCategory("");
          setDescription("");
          setPhotos([]);
          setSubmittedRequestId(null);
          setInitialMatches([]);
        }}
      />
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-20 md:pb-4">
      {/* Progress Bar */}
      <div className="sticky top-0 z-40 bg-white border-b shadow-sm">
        <div className="max-w-2xl mx-auto px-4 py-3">
          <div className="flex items-center justify-between mb-2">
            <button 
              onClick={() => step > 1 ? setStep(step - 1) : navigate("/")}
              className="p-2 -ml-2 text-gray-600 hover:text-gray-900"
            >
              <ArrowLeft className="h-5 w-5" />
            </button>
            <span className="text-sm font-medium text-gray-600">
              Schritt {step} von {totalSteps}
            </span>
            <div className="w-10" />
          </div>
          <div className="h-1 bg-gray-200 rounded-full overflow-hidden">
            <div 
              className="h-full bg-blue-600 transition-all duration-300"
              style={{ width: `${(step / totalSteps) * 100}%` }}
            />
          </div>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-4 py-6">
        {error && (
          <div className="mb-4 p-3 bg-red-50 text-red-700 rounded-lg text-sm">
            {error}
          </div>
        )}

        {/* Step 1: Category Selection */}
        {step === 1 && (
          <div>
            <h2 className="text-xl font-bold text-gray-900 mb-2">Was benötigen Sie?</h2>
            <p className="text-gray-600 mb-6">Wählen Sie eine Service-Kategorie</p>
            
            <div className="grid grid-cols-2 gap-3">
              {SERVICE_CATEGORIES.map((cat) => {
                const Icon = cat.icon;
                const isSelected = selectedCategory === cat.id;
                
                return (
                  <button
                    key={cat.id}
                    onClick={() => setSelectedCategory(cat.id)}
                    className={`p-4 rounded-xl border-2 transition-all text-left ${
                      isSelected 
                        ? "border-blue-600 bg-blue-50" 
                        : "border-gray-200 bg-white hover:border-blue-200"
                    }`}
                  >
                    <div className={`w-10 h-10 rounded-lg ${cat.color} flex items-center justify-center mb-2`}>
                      <Icon className="h-5 w-5" />
                    </div>
                    <span className={`font-medium text-sm ${isSelected ? "text-blue-700" : "text-gray-900"}`}>
                      {cat.name}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Step 2: Problem Description */}
        {step === 2 && (
          <div>
            <h2 className="text-xl font-bold text-gray-900 mb-2">Beschreiben Sie Ihr Anliegen</h2>
            <p className="text-gray-600 mb-6">Je detaillierter, desto besser können wir helfen</p>
            
            <Textarea
              placeholder="z.B. Die Steckdose in der Küche funktioniert nicht mehr. Es gibt manchmal einen Funken..."
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="min-h-[200px] text-base"
            />
            <p className="text-sm text-gray-500 mt-2">
              Mindestens 10 Zeichen ({description.length}/10)
            </p>
          </div>
        )}

        {/* Step 3: Address */}
        {step === 3 && (
          <div>
            <h2 className="text-xl font-bold text-gray-900 mb-2">Wo wird der Service benötigt?</h2>
            <p className="text-gray-600 mb-6">Geben Sie Ihre Adresse an</p>
            
            <div className="space-y-4">
              <div>
                <Label>Straße und Hausnummer</Label>
                <Input
                  value={address}
                  onChange={(e) => setAddress(e.target.value)}
                  placeholder="Musterstraße 123"
                  className="mt-1 h-12"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Postleitzahl *</Label>
                  <Input
                    value={postalCode}
                    onChange={(e) => setPostalCode(e.target.value.replace(/\D/g, "").slice(0, 5))}
                    placeholder="20095"
                    className="mt-1 h-12"
                  />
                </div>
                <div>
                  <Label>Stadt</Label>
                  <Input
                    value={city}
                    onChange={(e) => setCity(e.target.value)}
                    placeholder="Hamburg"
                    className="mt-1 h-12"
                  />
                </div>
              </div>
              
              <div className="flex items-center p-3 bg-blue-50 rounded-lg">
                <MapPin className="h-5 w-5 text-blue-600 mr-2" />
                <span className="text-sm text-blue-700">
                  Pilot-Region: Hamburg & Schleswig-Holstein
                </span>
              </div>
            </div>
          </div>
        )}

        {/* Step 4: Photos */}
        {step === 4 && (
          <div>
            <h2 className="text-xl font-bold text-gray-900 mb-2">Fotos hinzufügen (optional)</h2>
            <p className="text-gray-600 mb-6">Bilder helfen den Handwerkern, Ihr Problem besser zu verstehen</p>
            
            <div className="space-y-4">
              {photos.length > 0 && (
                <div className="grid grid-cols-3 gap-3">
                  {photos.map((photo, index) => (
                    <div key={index} className="relative aspect-square rounded-lg overflow-hidden bg-gray-100">
                      <img 
                        src={photo.preview} 
                        alt={`Foto ${index + 1}`}
                        className="w-full h-full object-cover"
                      />
                      <button
                        onClick={() => removePhoto(index)}
                        className="absolute top-1 right-1 p-1 bg-red-500 rounded-full text-white"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
              
              {photos.length < 5 && (
                <label className="block">
                  <div className="border-2 border-dashed border-gray-300 rounded-xl p-8 text-center cursor-pointer hover:border-blue-400 transition-colors">
                    <Camera className="h-10 w-10 text-gray-400 mx-auto mb-3" />
                    <p className="text-gray-600 font-medium">Foto aufnehmen oder hochladen</p>
                    <p className="text-sm text-gray-500 mt-1">Max. 5 Fotos, jeweils max. 10MB</p>
                  </div>
                  <input
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={handlePhotoUpload}
                    className="hidden"
                  />
                </label>
              )}
            </div>
          </div>
        )}

        {/* Step 5: Contact Details */}
        {step === 5 && (
          <div>
            <h2 className="text-xl font-bold text-gray-900 mb-2">Ihre Kontaktdaten</h2>
            <p className="text-gray-600 mb-6">So können die Handwerker Sie erreichen</p>
            
            <div className="space-y-4">
              <div>
                <Label>Name *</Label>
                <Input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Max Mustermann"
                  className="mt-1 h-12"
                  data-testid="request-form-name"
                />
              </div>
              
              <div>
                <Label>E-Mail *</Label>
                <Input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="ihre@email.de"
                  className="mt-1 h-12"
                  data-testid="request-form-email"
                />
              </div>
              
              <div>
                <Label>Telefon (optional)</Label>
                <Input
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="+49 123 456789"
                  className="mt-1 h-12"
                  data-testid="request-form-phone"
                />
              </div>
            </div>
          </div>
        )}

        {/* Step 6: Review & Submit */}
        {step === 6 && (
          <div>
            <h2 className="text-xl font-bold text-gray-900 mb-2">Anfrage überprüfen</h2>
            <p className="text-gray-600 mb-6">Überprüfen Sie Ihre Angaben vor dem Absenden</p>
            
            <div className="space-y-4">
              <Card>
                <CardContent className="p-4 space-y-3">
                  <div>
                    <span className="text-sm text-gray-500">Service</span>
                    <p className="font-medium">{SERVICE_CATEGORIES.find(c => c.id === selectedCategory)?.name}</p>
                  </div>
                  <div>
                    <span className="text-sm text-gray-500">Beschreibung</span>
                    <p className="font-medium">{description}</p>
                  </div>
                  <div>
                    <span className="text-sm text-gray-500">Adresse</span>
                    <p className="font-medium">{address || "-"}, {postalCode} {city}</p>
                  </div>
                  <div>
                    <span className="text-sm text-gray-500">Fotos</span>
                    <p className="font-medium">{photos.length} Foto(s)</p>
                  </div>
                  <div>
                    <span className="text-sm text-gray-500">Kontakt</span>
                    <p className="font-medium">{name}</p>
                    <p className="text-sm text-gray-600">{email}</p>
                    {phone && <p className="text-sm text-gray-600">{phone}</p>}
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        )}

        {/* Navigation Buttons */}
        <div className="mt-8 flex gap-3">
          {step > 1 && (
            <Button
              variant="outline"
              onClick={() => setStep(step - 1)}
              className="flex-1 h-12"
            >
              Zurück
            </Button>
          )}
          
          {step < totalSteps ? (
            <Button
              onClick={() => setStep(step + 1)}
              disabled={!canProceed()}
              className="flex-1 h-12 bg-blue-600 hover:bg-blue-700"
              data-testid="request-form-next"
            >
              Weiter <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          ) : (
            <Button
              onClick={handleSubmit}
              disabled={submitting}
              className="flex-1 h-12 bg-green-600 hover:bg-green-700"
              data-testid="request-form-submit"
            >
              {submitting ? "Wird gesendet..." : "Anfrage absenden"}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default RequestServicePage;
